import {
    ChannelLevelCurvesResponseDto,
    GetScenarioBasicInfoResponseDto,
    GetScenarioLevelResultComparisonResponseDto,
    UpdateScenarioRequestDto,
} from '@api-clients/media-plan';
import {
    Box,
    Center,
    Container,
    Grid,
    GridItem,
    HStack,
    SkeletonText,
    Spacer,
    Stack,
    Tag,
    TagLabel,
    Text,
    Wrap,
} from '@chakra-ui/react';
import {
    ExpandableBanner,
    ExpandableBannerBody,
    ExpandableBannerHeader,
} from '@components/atoms/expandableBanner/ExpandableBanner';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { getOptimisationObjective } from '@shared/cores/types';
import { useHelper } from '@shared/utils';
import { FC } from 'react';
import { ManageShareScenarioMenu } from '@apps/attentionADJUST/components/molecules/manageScenarioMenu/ManageShareScenarioMenu';
import { CampaignHeaderSectionForSharing } from '@components/organisms/campaignHeaderSection/CampaignHeaderSectionForSharing';

export interface SharedScenarioBannerProps {
    scenarioBasicInfo?: GetScenarioBasicInfoResponseDto;
    formatDetailsComponent?: ReactJSXElement;
    reachCurveData: Array<ChannelLevelCurvesResponseDto>;
    scenarioLevelResultsComparison?: GetScenarioLevelResultComparisonResponseDto;
    isFreePlan: boolean;
}

const SkeletonScenarioDetailText = () => (
    <Center height="1.5rem">
        <SkeletonText noOfLines={1} width="100%" />
    </Center>
);
export const SharedScenarioBanner: FC<SharedScenarioBannerProps> = ({
    scenarioBasicInfo,
    formatDetailsComponent = <></>,
    reachCurveData,
    scenarioLevelResultsComparison,
    isFreePlan,
}) => {
    let regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

    const { formatAgeGroup, formatStringToCapitalized, sortAgeGroups } = useHelper();

    const isLoaded = !!scenarioBasicInfo;

    return (
        <ExpandableBanner>
            <ExpandableBannerHeader>
                <Container>
                    <HStack>
                        <CampaignHeaderSectionForSharing
                            title={scenarioBasicInfo?.scenarioName ?? ''}
                            lastModified={scenarioBasicInfo?.updatedAt}
                            isLoading={!isLoaded}
                        />
                        <Spacer />
                        <ManageShareScenarioMenu
                            isFreePlan={isFreePlan}
                            reachCurveData={reachCurveData}
                            scenarioBasicInfo={scenarioBasicInfo}
                            scenarioLevelResultsComparison={scenarioLevelResultsComparison}
                        />
                    </HStack>
                </Container>
            </ExpandableBannerHeader>
            <ExpandableBannerBody autoOpen defaultIsOpen>
                <Container>
                    <Stack spacing="2rem" align="left">
                        <Grid templateColumns="repeat(4, 1fr)" gap="1rem">
                            <GridItem>
                                <Text>Budget</Text>
                                {isLoaded ? (
                                    <Text id="budget">{`$${scenarioBasicInfo?.budget?.toLocaleString()}`}</Text>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                            <GridItem>
                                <Text>Start Date</Text>
                                {isLoaded ? (
                                    <Text>
                                        {scenarioBasicInfo?.startDate?.toLocaleDateString()}
                                    </Text>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                            <GridItem>
                                <Text>End Date</Text>
                                {isLoaded ? (
                                    <Text>{scenarioBasicInfo?.endDate?.toLocaleDateString()}</Text>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                            <GridItem>
                                <Text>Budget Constraints</Text>
                                {isLoaded ? (
                                    <Text>
                                        {scenarioBasicInfo?.budgetConstraintType ===
                                        'adjustment_tolerance_level'
                                            ? `Plan Tolerance - ${formatStringToCapitalized(
                                                  scenarioBasicInfo.adjustmentToleranceLevel ?? '',
                                              )}`
                                            : 'Minimum Spend'}
                                    </Text>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                            <GridItem>
                                <Text>Countries</Text>
                                {isLoaded ? (
                                    <Wrap>
                                        {scenarioBasicInfo?.countries &&
                                            scenarioBasicInfo.countries.map((countryCode) => (
                                                <Tag key={countryCode}>
                                                    <TagLabel>
                                                        {regionNames.of(countryCode.toUpperCase())}
                                                    </TagLabel>
                                                </Tag>
                                            ))}
                                    </Wrap>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                            {/* Age group */}
                            <GridItem>
                                <Text>Age groups</Text>
                                {isLoaded ? (
                                    <Wrap>
                                        {sortAgeGroups(scenarioBasicInfo?.ageGroups).map(
                                            (ageGroup) => (
                                                <Tag key={ageGroup} colorScheme="green">
                                                    <TagLabel>
                                                        {formatAgeGroup(ageGroup, 'Group', true)}
                                                    </TagLabel>
                                                </Tag>
                                            ),
                                        )}
                                    </Wrap>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>

                            {/* Gender */}
                            <GridItem>
                                <Text>Genders</Text>
                                {isLoaded ? (
                                    <Wrap>
                                        {scenarioBasicInfo?.genders?.map((gender) => (
                                            <Tag key={gender} colorScheme="green">
                                                <TagLabel>
                                                    {formatStringToCapitalized(gender)}
                                                </TagLabel>
                                            </Tag>
                                        ))}
                                    </Wrap>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>

                            {/* Optimisation Objective */}
                            <GridItem>
                                <Text>Optimisation Objective</Text>
                                {isLoaded ? (
                                    <>
                                        {scenarioBasicInfo?.optimisationStrategy && (
                                            <Text>
                                                {getOptimisationObjective(
                                                    scenarioBasicInfo.optimisationStrategy,
                                                )}
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <SkeletonScenarioDetailText />
                                )}
                            </GridItem>
                        </Grid>
                        <Box>
                            <>{formatDetailsComponent}</>
                        </Box>
                    </Stack>
                </Container>
            </ExpandableBannerBody>
        </ExpandableBanner>
    );
};
